import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { addDays, format } from "date-fns";
import Collapse from "@material-ui/core/Collapse";
import Alert from "@material-ui/lab/Alert";
import { AlertTitle } from "@material-ui/lab";
import useBDocTranslator from "../hooks/useBDocTranslator";
import useUserState from "../hooks/useUserState";
import Axios from "axios";
import { MenuItem, TextField } from "@material-ui/core";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import SelectAppt from "../providers/SelectAppt";
import { BiBrain } from "react-icons/bi";
import { refinePrescriptionText } from "../../services/services";
import useDictation from "./dictation/useDictation";
import DictationPanel, {
  AI_COLOR,
  DictationStartButton,
} from "./dictation/DictationPanel";

export const prescTypes = [
  {
    label: "Pharmacy Prescription",
    value: "phar",
  },
  {
    label: "Diagnostic Imaging",
    value: "img",
  },
  {
    label: "Laboratory Tests",
    value: "lab",
  },
];

export const presTypeObjLabel = {
  phar: "Pharmacies",
  img: "Diagnostic imaging places",
  lab: "Laboratories",
};

// Barra del editor: formato de texto y botón de dictado. Quill la usa como
// contenedor de su toolbar (ver quillModules).
const PrescriptionToolbar = ({ dictation, showDictation }) => (
  <div id="prescription-ai-toolbar">
    <span className="ql-formats">
      <button className="ql-bold" />
      <button className="ql-italic" />
      <button className="ql-list" value="bullet" />
    </span>
    {showDictation && (
      <span className="ql-formats" style={{ width: "auto" }}>
        <DictationStartButton dictation={dictation} />
      </span>
    )}
  </div>
);

const escapeHtml = (text) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Cada línea del texto dictado como un párrafo de Quill.
const textToQuillHtml = (text) =>
  text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");

// Quill deja "<p><br></p>" cuando el editor está vacío.
const isQuillEmpty = (html) =>
  !(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const quillModules = {
  toolbar: {
    container: "#prescription-ai-toolbar",
  },
};

const quillFormats = ["bold", "italic", "list"];

// Atajos de vigencia de la receta, en días.
const VALIDITY_SHORTCUTS = [7, 15, 30];

const toInputDate = (date) => format(date, "yyyy-MM-dd");

const useStyles = makeStyles((theme) => ({
  form: {
    fontFamily: theme.typography.fontFamily,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  sectionTitle: {
    margin: 0,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  optionalTag: {
    marginLeft: theme.spacing(0.75),
    fontWeight: 400,
    textTransform: "none",
    letterSpacing: 0,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },
  validityRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  dateField: {
    width: 200,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  placeOption: {
    display: "flex",
    flexDirection: "column",
  },
  placeCity: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  editor: {
    "& .ql-container": {
      minHeight: 160,
      fontSize: "0.95rem",
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
    },
    "& #prescription-ai-toolbar": {
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
    },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  missing: {
    marginRight: "auto",
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
}));

export default function PrenscriptionDialog(props) {
  const classes = useStyles();
  const { t, language } = useBDocTranslator();
  const saveResponse = props.responseMessage;
  const [pharmacies, setPharmacies] = useState([]);
  const [prescType, setPrescType] = useState("select");
  const userLocalState = useUserState();
  const aiEnabled = userLocalState[0]?.extendedData?.ai_enabled || false;
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return (
      localStorage.getItem("prescription_dictation_onboarding_dismissed") !==
      "true"
    );
  });

  const handleDismissOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem(
      "prescription_dictation_onboarding_dismissed",
      "true"
    );
  };

  // Valor y callback más recientes del editor. El texto dictado se agrega al
  // contenido actual, no al que había cuando se empezó a dictar (antes se
  // perdía lo escrito mientras se dictaba o procesaba la IA).
  const valueRef = useRef(props.prescriptionValue);
  const handleNotesRef = useRef(props.handleNotes);
  useEffect(() => {
    valueRef.current = props.prescriptionValue;
    handleNotesRef.current = props.handleNotes;
  });

  const jwt = userLocalState[0]?.jwt;
  const refine = useCallback(
    (rawText) => refinePrescriptionText(rawText, language || "es", jwt),
    [language, jwt]
  );

  const handleInsertDictation = useCallback((text) => {
    const html = textToQuillHtml(text);
    if (!html) return;
    const current = valueRef.current || "";
    handleNotesRef.current(isQuillEmpty(current) ? html : current + html);
  }, []);

  const dictation = useDictation({
    language: language || "es",
    refine,
    onInsert: handleInsertDictation,
  });
  const showDictation = aiEnabled;

  useEffect(() => {
    Axios.get(`${process.env.REACT_APP_API_URL_BASE}/pharmacy/findAll`, {
      headers: { Authorization: "Bearer " + userLocalState[0].jwt },
    })
      .then((response) => {
        setPharmacies(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.log(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasType = !!prescType && prescType !== "select";
  const places = useMemo(
    () =>
      hasType
        ? pharmacies
            .filter((place) => place.type === prescType)
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        : [],
    [pharmacies, prescType, hasType]
  );
  const selectedPlace =
    places.find((place) => place.id === props?.pharmacy) || null;

  const today = toInputDate(new Date());
  const setValidityDays = (days) =>
    props.setDispenseTo(toInputDate(addDays(new Date(), days)));
  const selectedShortcut = VALIDITY_SHORTCUTS.find(
    (days) => props.dispenseTo === toInputDate(addDays(new Date(), days))
  );

  // El editor de Quill deja "<p><br></p>" cuando se borra todo el texto.
  const hasDirections = !!(props.prescriptionValue || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

  const missing = [
    !props.dispenseTo && t("Valid until"),
    !hasDirections && t("Prescription directions"),
  ].filter(Boolean);
  const canSave = missing.length === 0 && !props.isLoading;

  const errorMessage =
    saveResponse &&
    (saveResponse?.response?.data?.message ||
      saveResponse?.message ||
      (typeof saveResponse === "string" ? saveResponse : ""));

  return (
    <div className={classes.form}>
      {/* 1. Cita */}
      <section className={classes.section}>
        <h4 className={classes.sectionTitle}>{t("Appointment")}</h4>
        <SelectAppt
          onChange={(v) => props.setApptSelected(v)}
          value={props.apptSelected}
          patientId={props.patientId}
        />
      </section>

      {/* 2. Referir a un lugar */}
      <section className={classes.section}>
        <h4 className={classes.sectionTitle}>
          {t("Refer to")}
          <span className={classes.optionalTag}>({t("optional")})</span>
        </h4>
        <div className={classes.row}>
          <TextField
            id="prescription-types"
            select
            fullWidth
            variant="outlined"
            label={t("Prescription Type")}
            onChange={(e) => {
              setPrescType(e.target.value);
              props?.setPharmacy("select");
            }}
            value={prescType}
          >
            <MenuItem value="select">{t("Not referred")}</MenuItem>
            {prescTypes.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {t(item.label)}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            id="pharmacy-list"
            fullWidth
            disabled={!hasType}
            options={places}
            value={selectedPlace}
            getOptionLabel={(place) =>
              place ? [place.name, place.city].filter(Boolean).join(" - ") : ""
            }
            getOptionSelected={(option, current) => option.id === current?.id}
            onChange={(event, place) =>
              props?.setPharmacy(place ? place.id : "select")
            }
            noOptionsText={t("No places found")}
            renderOption={(place) => (
              <div className={classes.placeOption}>
                <span>{place.name}</span>
                {place.city && (
                  <span className={classes.placeCity}>{place.city}</span>
                )}
              </div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={
                  hasType ? t(presTypeObjLabel[prescType]) : t("Place")
                }
                placeholder={hasType ? t("Search by name or city") : ""}
                helperText={
                  hasType ? "" : t("Choose a prescription type first")
                }
              />
            )}
          />
        </div>
      </section>

      {/* 3. Vigencia */}
      <section className={classes.section}>
        <h4 className={classes.sectionTitle}>{t("Valid until")}</h4>
        <div className={classes.validityRow}>
          <TextField
            id="dispense-to"
            type="date"
            variant="outlined"
            size="small"
            className={classes.dateField}
            value={props.dispenseTo || ""}
            onChange={(e) => props.setDispenseTo(e.target.value)}
            inputProps={{ min: today }}
          />
          {VALIDITY_SHORTCUTS.map((days) => (
            <Chip
              key={days}
              clickable
              color={selectedShortcut === days ? "primary" : "default"}
              variant={selectedShortcut === days ? "default" : "outlined"}
              label={`${days} ${t("days")}`}
              onClick={() => setValidityDays(days)}
            />
          ))}
        </div>
      </section>

      {/* 4. Indicaciones */}
      <section className={classes.section}>
        <h4 className={classes.sectionTitle}>{t("Prescription directions")}</h4>
        {showDictation && dictation.isSupported && (
          <Collapse in={showOnboarding && dictation.phase === "idle"}>
            <Alert
              severity="info"
              icon={<BiBrain size={20} style={{ color: AI_COLOR }} />}
              onClose={handleDismissOnboarding}
              style={{
                backgroundColor: "rgba(123, 44, 191, 0.04)",
                border: "1px solid rgba(123, 44, 191, 0.15)",
              }}
            >
              <AlertTitle
                style={{
                  color: AI_COLOR,
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                {t("AI Voice Dictation")}
              </AlertTitle>
              <span style={{ fontSize: "12px" }}>
                {t(
                  "Press Dictate with AI, speak normally and press Stop when you finish. You will review the text before it is inserted."
                )}
              </span>
            </Alert>
          </Collapse>
        )}
        {showDictation && <DictationPanel dictation={dictation} />}
        <div className={classes.editor}>
          <PrescriptionToolbar
            dictation={dictation}
            showDictation={showDictation}
          />
          <ReactQuill
            theme="snow"
            value={props.prescriptionValue}
            onChange={props.handleNotes}
            modules={quillModules}
            formats={quillFormats}
            placeholder={t("Medication, dose, frequency and duration")}
          />
        </div>
      </section>

      {errorMessage && (
        <Alert severity="error">
          {t("The prescription could not be saved")}: {errorMessage}
        </Alert>
      )}

      <div className={classes.footer}>
        {missing.length > 0 && (
          <span className={classes.missing}>
            {t("Missing")}: {missing.join(", ")}
          </span>
        )}
        {props.handleCancel && (
          <Button onClick={props.handleCancel} disabled={props.isLoading}>
            {t("Cancel")}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={props.handleSave}
          disabled={!canSave}
        >
          {props.isLoading ? t("Saving...") : t("Save prescription")}
        </Button>
      </div>
    </div>
  );
}
